import React, { useState, useEffect, useCallback } from 'react';
import {
  createClient,
  extendClientTimer,
  toggleClientAccess,
  updateSitePreviewUrl,
  updateApprovalBox,
  updateClient,
  deleteClient,
  getClientStats,
  getTimeRemaining,
  isTimerExpired,
  subscribeToClients,
} from '../../services/clientService';

const STATUS_LABELS = {
  pending: { text: 'Pending', color: 'bg-gray-100 text-gray-800' },
  approved: { text: 'Approved', color: 'bg-blue-100 text-blue-800' },
  deposit_paid: { text: 'Deposit Paid', color: 'bg-green-100 text-green-800' },
  active: { text: 'Active', color: 'bg-green-100 text-green-800' },
  expired: { text: 'Expired', color: 'bg-red-100 text-red-800' },
  converted: { text: 'Converted', color: 'bg-purple-100 text-purple-800' },
  pending_deposit: { text: 'Pending Deposit', color: 'bg-yellow-100 text-yellow-800' },
  in_progress: { text: 'In Progress', color: 'bg-blue-100 text-blue-800' },
  review: { text: 'Under Review', color: 'bg-purple-100 text-purple-800' },
  completed: { text: 'Completed', color: 'bg-green-100 text-green-800' },
};

const APPROVAL_BOXES = [
  { key: 1, label: 'Site Quality' },
  { key: 2, label: 'Content Accuracy' },
  { key: 3, label: 'Ready for Outreach' },
];

/** Color-coded timer based on remaining time */
const getTimerColor = (timeRemaining) => {
  if (!timeRemaining || timeRemaining.expired) return 'text-gray-400';
  const hoursLeft = timeRemaining.hours + (timeRemaining.minutes / 60);
  if (hoursLeft > 24) return 'text-green-600';
  if (hoursLeft > 6) return 'text-yellow-600';
  return 'text-red-600';
};

const getTimerBg = (timeRemaining) => {
  if (!timeRemaining || timeRemaining.expired) return 'bg-gray-100';
  const hoursLeft = timeRemaining.hours + (timeRemaining.minutes / 60);
  if (hoursLeft > 24) return 'bg-green-50';
  if (hoursLeft > 6) return 'bg-yellow-50';
  return 'bg-red-50';
};

export default function ClientManagement() {
  const [clients, setClients] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [extendHours, setExtendHours] = useState(24);
  const [extendReason, setExtendReason] = useState('');
  const [showExtendModal, setShowExtendModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [timers, setTimers] = useState({});

  // New client form state
  const [newClient, setNewClient] = useState({
    companyName: '',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    industry: '',
    currentWebsite: '',
    projectType: 'website',
    servicesRequested: [],
    projectNotes: '',
    sitePreviewUrl: '',
  });

  const loadStats = useCallback(async () => {
    try {
      const statsData = await getClientStats();
      setStats(statsData);
    } catch (err) {
      console.error('Error loading stats:', err);
    }
  }, []);

  // Real-time subscription to clients collection
  useEffect(() => {
    const unsubscribe = subscribeToClients((clientsData) => {
      setClients(clientsData);
      setLoading(false);
    });
    loadStats();
    return () => unsubscribe();
  }, [loadStats]);

  const loadData = useCallback(() => {
    loadStats();
  }, [loadStats]);

  // Live timer updates
  useEffect(() => {
    const tick = () => {
      const updated = {};
      clients.forEach(c => {
        if (c.timerExpiresAt && !isTimerExpired(c.timerExpiresAt)) {
          updated[c.id] = getTimeRemaining(c.timerExpiresAt);
        }
      });
      setTimers(updated);
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [clients]);

  // ── Actions ──

  const handleCreateClient = async (e) => {
    e.preventDefault();
    setActionLoading('create');
    try {
      const created = await createClient(newClient);
      alert(`Client created!\nAccess Code: ${created.accessCode}\nShare this with the client after their demo.`);
      setShowAddForm(false);
      setNewClient({ companyName: '', contactName: '', contactEmail: '', contactPhone: '', industry: '', currentWebsite: '', projectType: 'website', servicesRequested: [], projectNotes: '', sitePreviewUrl: '' });
      loadData();
    } catch (err) {
      alert('Error creating client: ' + err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleExtendTimer = async () => {
    if (!selectedClient) return;
    setActionLoading('extend');
    try {
      await extendClientTimer(selectedClient.id, extendHours, extendReason);
      setShowExtendModal(false);
      setExtendHours(24);
      setExtendReason('');
      loadData();
    } catch (err) {
      alert('Error extending timer: ' + err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleToggleAccess = async (client) => {
    setActionLoading(client.id);
    try {
      await toggleClientAccess(client.id);
      loadData();
    } catch (err) {
      alert('Error toggling access: ' + err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleApprovalChange = async (client, boxNumber, checked) => {
    setActionLoading(client.id + '-approval');
    try {
      await updateApprovalBox(client.id, boxNumber, checked);
      loadData();
    } catch (err) {
      alert('Error updating approval: ' + err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleUpdatePreviewUrl = async (client) => {
    const url = prompt('Enter the site preview URL:', client.sitePreviewUrl || '');
    if (url === null) return;
    try {
      await updateSitePreviewUrl(client.id, url);
      loadData();
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  const handleToggleRequireDeposit = async (client) => {
    setActionLoading(client.id + '-deposit');
    try {
      await updateClient(client.id, { requireDeposit: !client.requireDeposit });
      loadData();
    } catch (err) {
      alert('Error toggling deposit requirement: ' + err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteClient = async (client) => {
    if (!window.confirm(`Delete client "${client.companyName}"? This cannot be undone.`)) return;
    try {
      await deleteClient(client.id);
      loadData();
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  const serviceOptions = ['Website Development', 'AI Integration', 'Marketing Consulting', 'Business Development', 'E-commerce', 'CRM Integration'];

  if (loading) {
    return <div className="text-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-apn-blue mx-auto"></div></div>;
  }

  return (
    <div className="space-y-6">
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Total Clients', value: stats.total, color: 'text-gray-900' },
            { label: 'Active', value: stats.active, color: 'text-green-600' },
            { label: 'Pending Deposit', value: stats.pendingDeposit, color: 'text-yellow-600' },
            { label: 'Expiring < 24h', value: stats.expiringWithin24h, color: 'text-red-600' },
          ].map(s => (
            <div key={s.label} className="bg-white rounded-lg shadow p-4 text-center">
              <div className={`text-2xl font-bold ${s.color}`}>{s.value}</div>
              <div className="text-xs text-gray-500 mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Header + Add Button */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">Prospect Clients</h3>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="px-4 py-2 bg-apn-blue text-white rounded-lg hover:bg-apn-dark-blue transition-colors text-sm font-medium"
        >
          {showAddForm ? 'Cancel' : '+ Add Client'}
        </button>
      </div>

      {/* Add Client Form */}
      {showAddForm && (
        <div className="bg-white rounded-lg shadow p-6">
          <h4 className="font-semibold text-gray-900 mb-4">New Client (Post-Demo)</h4>
          <form onSubmit={handleCreateClient} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input required placeholder="Company Name *" value={newClient.companyName} onChange={e => setNewClient(p => ({ ...p, companyName: e.target.value }))} className="px-3 py-2 border border-gray-300 rounded-md text-sm" />
            <input required placeholder="Contact Name *" value={newClient.contactName} onChange={e => setNewClient(p => ({ ...p, contactName: e.target.value }))} className="px-3 py-2 border border-gray-300 rounded-md text-sm" />
            <input required type="email" placeholder="Contact Email *" value={newClient.contactEmail} onChange={e => setNewClient(p => ({ ...p, contactEmail: e.target.value }))} className="px-3 py-2 border border-gray-300 rounded-md text-sm" />
            <input placeholder="Phone" value={newClient.contactPhone} onChange={e => setNewClient(p => ({ ...p, contactPhone: e.target.value }))} className="px-3 py-2 border border-gray-300 rounded-md text-sm" />
            <input placeholder="Industry" value={newClient.industry} onChange={e => setNewClient(p => ({ ...p, industry: e.target.value }))} className="px-3 py-2 border border-gray-300 rounded-md text-sm" />
            <input placeholder="Current Website URL" value={newClient.currentWebsite} onChange={e => setNewClient(p => ({ ...p, currentWebsite: e.target.value }))} className="px-3 py-2 border border-gray-300 rounded-md text-sm" />
            <select value={newClient.projectType} onChange={e => setNewClient(p => ({ ...p, projectType: e.target.value }))} className="px-3 py-2 border border-gray-300 rounded-md text-sm">
              <option value="website">Website Development</option>
              <option value="ai_integration">AI Integration</option>
              <option value="both">Website + AI</option>
            </select>
            <input placeholder="Site Preview URL (optional)" value={newClient.sitePreviewUrl} onChange={e => setNewClient(p => ({ ...p, sitePreviewUrl: e.target.value }))} className="px-3 py-2 border border-gray-300 rounded-md text-sm" />
            <div className="md:col-span-2">
              <label className="block text-xs text-gray-500 mb-1">Services Requested</label>
              <div className="flex flex-wrap gap-2">
                {serviceOptions.map(svc => (
                  <label key={svc} className="inline-flex items-center text-sm">
                    <input
                      type="checkbox"
                      checked={newClient.servicesRequested.includes(svc)}
                      onChange={e => {
                        setNewClient(p => ({
                          ...p,
                          servicesRequested: e.target.checked
                            ? [...p.servicesRequested, svc]
                            : p.servicesRequested.filter(s => s !== svc),
                        }));
                      }}
                      className="mr-1"
                    />
                    {svc}
                  </label>
                ))}
              </div>
            </div>
            <div className="md:col-span-2">
              <textarea placeholder="Project Notes" value={newClient.projectNotes} onChange={e => setNewClient(p => ({ ...p, projectNotes: e.target.value }))} rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
            </div>
            <div className="md:col-span-2 flex justify-end">
              <button type="submit" disabled={actionLoading === 'create'} className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium disabled:opacity-50">
                {actionLoading === 'create' ? 'Creating...' : 'Create Client & Generate Access Code'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Client List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Approval</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Deposit</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Timer</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Access</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {clients.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-500">No clients yet. Add one after a demo.</td></tr>
              ) : (
                clients.map(client => {
                  const statusKey = client.status || client.projectStatus || 'pending';
                  const status = STATUS_LABELS[statusKey] || STATUS_LABELS.pending;
                  const timer = timers[client.id];
                  const expired = !timer || timer.expired;
                  const allApproved = client.approvalBox1 && client.approvalBox2 && client.approvalBox3;

                  return (
                    <tr key={client.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-gray-900">{client.companyName}</div>
                        <div className="text-xs text-gray-500">{client.industry}</div>
                        {client.previewUrl && (
                          <a href={client.previewUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-apn-blue hover:underline">Preview ↗</a>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-gray-900">{client.contactName}</div>
                        <div className="text-xs text-gray-500">{client.contactEmail}</div>
                        <div className="text-xs font-mono text-apn-blue mt-0.5">Code: {client.accessCode}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="space-y-1">
                          {APPROVAL_BOXES.map(box => (
                            <label key={box.key} className="flex items-center space-x-1 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={!!client[`approvalBox${box.key}`]}
                                onChange={e => handleApprovalChange(client, box.key, e.target.checked)}
                                disabled={actionLoading === client.id + '-approval'}
                                className="rounded border-gray-300 text-apn-blue focus:ring-apn-blue h-3.5 w-3.5"
                              />
                              <span className="text-xs text-gray-600">{box.label}</span>
                            </label>
                          ))}
                          {allApproved && <span className="text-xs text-green-600 font-medium">✓ Approved</span>}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-medium rounded-full px-2 py-1 ${status.color}`}>
                          {status.text}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <label className="flex items-center space-x-1 cursor-pointer mb-1">
                          <input
                            type="checkbox"
                            checked={!!client.requireDeposit}
                            onChange={() => handleToggleRequireDeposit(client)}
                            disabled={actionLoading === client.id + '-deposit'}
                            className="rounded border-gray-300 text-apn-blue focus:ring-apn-blue h-3.5 w-3.5"
                          />
                          <span className="text-xs text-gray-600">Require $5</span>
                        </label>
                        {client.requireDeposit && (
                          client.depositPaid ? (
                            <span className="text-xs text-green-600 font-medium">$5 Paid ✓</span>
                          ) : (
                            <span className="text-xs text-yellow-600 font-medium">Awaiting payment</span>
                          )
                        )}
                        {!client.requireDeposit && (
                          <span className="text-xs text-gray-400 font-medium">No deposit needed</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {client.timerExpiresAt ? (
                          expired ? (
                            <span className={`text-xs font-medium px-2 py-1 rounded ${getTimerBg(timer)} ${getTimerColor(timer)}`}>Expired</span>
                          ) : (
                            <span className={`text-xs font-mono font-medium px-2 py-1 rounded ${getTimerBg(timer)} ${getTimerColor(timer)}`}>{timer?.display}</span>
                          )
                        ) : (
                          <span className="text-xs text-gray-400">Not started</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => handleToggleAccess(client)}
                          disabled={actionLoading === client.id}
                          className={`text-xs font-medium px-2 py-1 rounded ${
                            client.accessEnabled
                              ? 'bg-green-100 text-green-700 hover:bg-green-200'
                              : 'bg-red-100 text-red-700 hover:bg-red-200'
                          }`}
                        >
                          {client.accessEnabled ? 'ON' : 'OFF'}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center space-x-1">
                          <button
                            onClick={() => { setSelectedClient(client); setShowExtendModal(true); }}
                            className="text-xs px-2 py-1 bg-blue-50 text-blue-700 rounded hover:bg-blue-100"
                            title="Extend Timer"
                          >
                            ⏱ Extend
                          </button>
                          <button
                            onClick={() => handleUpdatePreviewUrl(client)}
                            className="text-xs px-2 py-1 bg-gray-50 text-gray-700 rounded hover:bg-gray-100"
                            title="Set Preview URL"
                          >
                            🔗 URL
                          </button>
                          <button
                            onClick={() => handleDeleteClient(client)}
                            className="text-xs px-2 py-1 bg-red-50 text-red-700 rounded hover:bg-red-100"
                            title="Delete"
                          >
                            ✕
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Extend Timer Modal */}
      {showExtendModal && selectedClient && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <h4 className="text-lg font-semibold text-gray-900 mb-4">
              Extend Timer — {selectedClient.companyName}
            </h4>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-600 mb-1">Additional Hours</label>
                <div className="flex space-x-2">
                  {[12, 24, 48, 72].map(h => (
                    <button
                      key={h}
                      onClick={() => setExtendHours(h)}
                      className={`px-3 py-1 rounded text-sm ${extendHours === h ? 'bg-apn-blue text-white' : 'bg-gray-100 text-gray-700'}`}
                    >
                      {h}h
                    </button>
                  ))}
                  <input
                    type="number"
                    min={1}
                    max={720}
                    value={extendHours}
                    onChange={e => setExtendHours(parseInt(e.target.value) || 24)}
                    className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Reason (optional)</label>
                <input
                  value={extendReason}
                  onChange={e => setExtendReason(e.target.value)}
                  placeholder="e.g., Client requested more review time"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
              </div>
              <div className="flex justify-end space-x-3 pt-2">
                <button
                  onClick={() => { setShowExtendModal(false); setSelectedClient(null); }}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={handleExtendTimer}
                  disabled={actionLoading === 'extend'}
                  className="px-4 py-2 bg-apn-blue text-white rounded-lg hover:bg-apn-dark-blue text-sm disabled:opacity-50"
                >
                  {actionLoading === 'extend' ? 'Extending...' : `Extend by ${extendHours}h`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
